import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ClockIcon, CheckBadgeIcon, XCircleIcon, SparklesIcon } from '@heroicons/react/24/solid';
import { useLocation } from 'react-router-dom'; // Import useLocation

const StatusCard = ({ status, children, icon }) => {
    const colorClasses = {
        pending: 'bg-yellow-100 dark:bg-yellow-900/50 border-yellow-500 text-yellow-800 dark:text-yellow-200',
        approved: 'bg-green-100 dark:bg-green-900/50 border-green-500 text-green-800 dark:text-green-200',
        rejected: 'bg-red-100 dark:bg-red-900/50 border-red-500 text-red-800 dark:text-red-200',
        purchased: 'bg-blue-100 dark:bg-blue-900/50 border-blue-500 text-blue-800 dark:text-blue-200',
    };
    return (
        <div className={`p-6 rounded-lg border-l-4 ${colorClasses[status] || 'bg-gray-100'}`}>
            <div className="flex items-center">
                {icon}
                <h3 className="font-bold text-xl ml-3 capitalize">{status}</h3>
            </div>
            <div className="mt-2 pl-10 text-base">{children}</div>
        </div>
    );
};

export default function CampaignStatusPage() {
    const { token } = useAuth();
    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const location = useLocation();
    const isSidebarOpen = location?.state?.isSidebarOpen; // Access isSidebarOpen from location state, so it's passed in the routes

    useEffect(() => {
        const fetchStatus = async () => {
            if (!token) {
                setLoading(false);
                return;
            }
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/campaigns/my-status`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) throw new Error('Could not fetch your campaign status.');
                const data = await response.json();
                setCampaign(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchStatus();
    }, [token]);

    if (loading) return <div className="p-8 text-center text-xl">Loading Campaign Status...</div>;
    if (error) return <div className="p-8 text-center text-xl text-red-500">{error}</div>;
    if (!campaign) return <div className="p-8 text-center text-xl">You have not created a campaign yet.</div>;

    return (
        <div className={`p-6 md:p-10 overflow-auto ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
            <h1 className="text-3xl font-extrabold mb-4 text-gray-900 dark:text-white">Your Campaign Status</h1>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg space-y-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{campaign.product_name}</h2>
                    <p className="text-gray-500 dark:text-gray-400 mt-2">Your reason for request:</p>
                    <blockquote className="border-l-4 border-orange-500 pl-4 italic my-2 text-gray-600 dark:text-gray-300">{campaign.reason}</blockquote>
                </div>

                <div className="mt-4">
                    {campaign.status === 'pending' && (
                        <StatusCard status="pending" icon={<ClockIcon className="w-8 h-8"/>}>
                            Your request is currently under review by our team. You will be notified once a decision has been made.
                        </StatusCard>
                    )}
                    {campaign.status === 'approved' && (
                        <StatusCard status="approved" icon={<CheckBadgeIcon className="w-8 h-8"/>}>
                            Congratulations! Your campaign has been approved and is now live on our site for donors to see and support.
                        </StatusCard>
                    )}
                    {campaign.status === 'rejected' && (
                        <StatusCard status="rejected" icon={<XCircleIcon className="w-8 h-8"/>}>
                            We're sorry, but your campaign request was not approved at this time.
                            {campaign.admin_notes && (
                                <div className="mt-2 pt-2 border-t border-red-200 dark:border-red-700">
                                    <p className="font-semibold">Admin Feedback:</p>
                                    <p>{campaign.admin_notes}</p>
                                </div>
                            )}
                        </StatusCard>
                    )}
                    {/* ✅ NEW: UI for the 'purchased' status */}
                    {campaign.status === 'purchased' && (
                        <StatusCard status="purchased" icon={<SparklesIcon className="w-8 h-8"/>}>
                            Wonderful news! A generous donor has funded your request. We will be in contact with you shortly with instructions on how to receive your item.
                        </StatusCard>
                    )}
                </div>
            </div>
        </div>
    );
}